import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Package, FolderTree, Car, Warehouse,
  ClipboardList, Users, Settings, LogOut, Activity, Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import DijitalRoketFooter from "@/components/common/DijitalRoketFooter";
import Logo from "@/components/common/Logo";
import { APP_VERSION } from "@/version";

const menu = [
  { to: "/admin", end: true, label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/firmalar", label: "Firmalar", icon: Building2, testid: "nav-companies" },
  { to: "/admin/urunler", label: "Ürünler", icon: Package, testid: "nav-products" },
  { to: "/admin/kategoriler", label: "Kategoriler", icon: FolderTree, testid: "nav-categories" },
  { to: "/admin/taksonomi", label: "Marka / Model", icon: Car, testid: "nav-taxonomy" },
  { to: "/admin/stok", label: "Stok", icon: Warehouse, testid: "nav-stock" },
  { to: "/admin/siparisler", label: "Siparişler", icon: ClipboardList, testid: "nav-orders" },
  { to: "/admin/kullanicilar", label: "Kullanıcılar", icon: Users, testid: "nav-users" },
  { to: "/admin/hareketler", label: "Hareketler", icon: Activity, testid: "nav-audit-logs" },
  { to: "/admin/ayarlar", label: "Ayarlar", icon: Settings, testid: "nav-settings" },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <div className="h-full flex flex-col">
      <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-800 shrink-0">
        <Link to="/admin" onClick={onNavigate} data-testid="logo-home-link" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <Logo size="sm" />
          <div className="ml-1 text-[10px] uppercase tracking-widest text-slate-500 border-l border-slate-800 pl-3">Admin<br/>Panel</div>
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {menu.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            end={m.end}
            onClick={onNavigate}
            data-testid={m.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors ${
                isActive
                  ? "bg-slate-800 text-slate-50 border-l-2 border-red-500 pl-2"
                  : "text-slate-400 hover:text-slate-50 hover:bg-slate-800/60"
              }`
            }
          >
            <m.icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{m.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <span className="text-[10px] text-slate-600 mono flex-shrink-0" data-testid="app-version-sidebar">v{APP_VERSION}</span>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-300 hover:bg-red-900/30 hover:text-red-400"
          onClick={onLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" /> Güvenli Çıkış
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  // Rota değiştiğinde mobil menüyü kapat
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const currentPageLabel = menu.find((m) => (m.end ? loc.pathname === m.to : loc.pathname.startsWith(m.to)))?.label || "Admin";

  return (
    <div className="min-h-screen bg-slate-950 grain-bg flex-1 flex flex-col w-full">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-slate-800 bg-slate-950/95 backdrop-blur-xl flex-col z-40">
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 h-14 border-b border-slate-800 bg-slate-950/95 backdrop-blur-xl flex items-center gap-3 px-3">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-slate-200 hover:bg-slate-800" data-testid="mobile-menu-trigger">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-slate-950 border-slate-800 text-slate-50">
            <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <Link to="/admin" className="flex items-center gap-2 min-w-0">
          <Logo size="sm" />
          <span className="text-xs uppercase tracking-widest text-slate-400 truncate">{currentPageLabel}</span>
        </Link>
      </header>

      {/* Content */}
      <main className="lg:pl-64 flex-1 flex flex-col min-h-screen w-full">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full flex-1 min-w-0">
          <Outlet />
        </div>
        <footer className="mt-auto border-t border-slate-800/50 py-4 px-4 sm:px-6 lg:px-8">
          <DijitalRoketFooter compact />
        </footer>
      </main>
    </div>
  );
}
